import React, {useCallback, useMemo, useState} from 'react'

import {useRouter} from 'next/router'
import {useLocalStorage} from 'react-use'
import styled from 'styled-components'
import {media} from 'styled-bootstrap-grid'
import {TransformWrapper, TransformComponent} from 'react-zoom-pan-pinch'

import {ProductDetail, Variant} from '@festi/common/api/rest'
import {FullScreenModal} from '@festi/common/components/common'
import {fluidRange} from '@festi/common/utils/styles'

import ProductTags from 'src/components/cards/redesign/utilities/ProductTags'
import EmblaCarousel from './EmblaCarousel'
import WebpImage from '../../WebpImage'

export interface ArrowBtnProps {
  bottom?: boolean
  left?: boolean
  right?: boolean
}

const GalleryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  ${fluidRange('gap', '16px', '40px')}
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const SlideImage = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-height: 100%;
  margin: 0 auto;
  padding: 4px;

  &:before {
    content: '';
    display: block;
    padding-bottom: 100%;
  }

  &.thumb {
    height: 100%;
  }
`

const MainSlide = styled(SlideImage)`
  height: 100%;
  max-height: calc(60vh - 160px);
  margin: auto;

  ${media.md`
    max-height: 700px;
  `}
`

const FullScreenWrapper = styled.div`
  height: 100%;
  padding: 20px;
`

const FullScreenMainImageWrapper = styled.div`
  width: 100%;
  height: 100%;

  .react-transform-wrapper {
    width: 100%;
    height: 100%;

    > div {
      width: 100%;
      height: 100%;
    }
  }
`

interface Props {
  variant?: Variant
  product?: ProductDetail
}

export default function ProductImageGallery({
  variant,
  product,
}: Props): JSX.Element {
  const router = useRouter()
  const [previousRoute, setPreviousRoute, removePreviousRoute] =
    useLocalStorage<string>('previousRoute')

  const [currSlide, setCurrSlide] = useState<number | undefined>(undefined)
  const [isZoomed, setIsZoomed] = useState<boolean>(false)

  const isOpenFullScreen =
    !!router.query.fullScreenGallery &&
    router.query.fullScreenGallery === 'true'

  const mainImageArray = useMemo(() => {
    const imageArray = variant.images.map((img) => (
      <MainSlide key={img.id}>
        <WebpImage
          webp={img?.image?.webpS}
          jpg={img?.image?.jpgS}
          alt={img?.alt || variant.name}
        />
      </MainSlide>
    ))
    return imageArray
  }, [variant.images, variant.name])

  const handleOnTransformed = useCallback(
    (scale: number) => {
      if (scale > 1 && !isZoomed) {
        setIsZoomed(true)
      } else if (scale <= 1 && isZoomed) {
        setIsZoomed(false)
      }
    },
    [isZoomed],
  )

  const handleOnCloseModal = useCallback(() => {
    if (previousRoute) {
      router.back()
    } else {
      const {pathname, query} = router
      delete router.query.fullScreenGallery
      router.push({pathname, query}, undefined, {shallow: true})
    }

    setIsZoomed(false)
  }, [previousRoute, router])

  const mainFullScreenImageArray = useMemo(() => {
    const imageArray = variant.images.map((img) => (
      <FullScreenMainImageWrapper key={img.id}>
        <TransformWrapper
          pinch={{step: 1, excluded: ['pinchDisabled']}}
          wheel={{step: 1, excluded: ['pinchDisabled']}}
          panning={{disabled: !isZoomed, excluded: ['panningDisabled']}}
          doubleClick={{step: 1, mode: 'toggle'}}
          onTransformed={(event) => handleOnTransformed(event.state.scale)}
        >
          <TransformComponent>
            <SlideImage>
              <WebpImage
                webp={img?.image?.webpL}
                jpg={img?.image?.jpgL}
                alt={img?.alt || variant.name}
              />
            </SlideImage>
          </TransformComponent>
        </TransformWrapper>
      </FullScreenMainImageWrapper>
    ))
    return imageArray
  }, [handleOnTransformed, isZoomed, variant.images, variant.name])

  const miniImageArray = useMemo(() => {
    const imageArray = variant.images.map((img) => (
      <SlideImage key={img.id} className="thumb">
        <WebpImage
          webp={img?.image?.webpXs}
          jpg={img?.image?.jpgXs}
          alt={img?.alt || variant.name}
        />
      </SlideImage>
    ))
    return imageArray
  }, [variant.images, variant.name])

  return (
    <GalleryContainer>
      <Wrapper>
        <ProductTags variant={variant} product={product} isProductPage />

        <EmblaCarousel
          slides={mainImageArray}
          thumbSlides={miniImageArray}
          showDotButtons
          setFullScreenStartSlide={setCurrSlide}
          setPreviousRoute={setPreviousRoute}
        />
      </Wrapper>
      <FullScreenModal
        isOpen={isOpenFullScreen}
        onRequestClose={handleOnCloseModal}
      >
        <FullScreenWrapper>
          <EmblaCarousel
            slides={mainFullScreenImageArray}
            thumbSlides={miniImageArray}
            fullScreenStartSlide={currSlide}
            setFullScreenStartSlide={setCurrSlide}
            removePreviousRoute={removePreviousRoute}
            showArrowButtons
            isFullScreen
            isZoomed={isZoomed}
          />
        </FullScreenWrapper>
      </FullScreenModal>
    </GalleryContainer>
  )
}
